import React from "react";
import {
  CoffeeIcon,
  CalendarIcon,
  ClockIcon,
  NotesIcon,
  PenIcon,
  PlusIcon,
} from "../../Icons";
import { formatTimeAmPm } from "../../utils/dateUtils";
import { DASHBOARD_BTN, DASHBOARD_TABLE, getTimeCardColWidths } from "./dashboardTheme";
import { SHOW_SESSION_REMARK_UI } from "./sessionRemarkFeature";

const TimeCardTable = ({
  days = [],
  summary = {},
  onEdit,
  onAdd,
  /** @deprecated Remark UI gated by SHOW_SESSION_REMARK_UI — keep props for future re-enable */
  onRemarkEdit,
  canEditRemark = false,
  userName,
  /** Payroll modal — hides duplicate user header */
  embedded = false,
}) => {
  /** Parse YYYY-MM-DD as local date (no timezone conversion) */
  const formatDate = (dateString) => {
    if (!dateString)
      return {
        shortWeekday: "-",
        day: 0,
        monthShort: "-",
        year: 0,
      };
    const parts = String(dateString).split("T")[0].split("-").map(Number);
    if (parts.length < 3)
      return {
        shortWeekday: "-",
        day: 0,
        monthShort: "-",
        year: 0,
      };
    const [y, m, d] = parts;
    const date = new Date(y, m - 1, d);
    return {
      shortWeekday: date.toLocaleDateString("en-US", { weekday: "short" }),
      day: date.getDate(),
      monthShort: date.toLocaleDateString("en-US", { month: "short" }),
      year: date.getFullYear(),
    };
  };

  const formatTime = (isoString) => formatTimeAmPm(isoString);

  const renderBreaks = (breaks) => {
    if (!breaks || breaks.length === 0) {
      return <span className="text-slate-400">—</span>;
    }

    return (
      <div className="flex flex-wrap items-center gap-0.5 min-w-0">
        {breaks.map((brk, idx) => (
          <span
            key={brk.id ?? idx}
            className="inline-flex items-center gap-1 text-[13px] text-slate-600 bg-amber-50/80 border border-amber-100 rounded px-1.5 py-0.5 whitespace-nowrap"
          >
            <CoffeeIcon className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span>
              {formatTime(brk.breakInTime)}–{formatTime(brk.breakOutTime)}
            </span>
            {brk.duration ? (
              <span className="text-amber-700 font-medium">({brk.duration})</span>
            ) : null}
          </span>
        ))}
      </div>
    );
  };

  /* --- Session remark helpers (kept for future; only used when SHOW_SESSION_REMARK_UI) --- */
  const getRemarkText = (session) => String(session?.remark ?? "").trim();

  const renderRemarkText = (session) => {
    const remarkText = getRemarkText(session);
    if (!remarkText) {
      return <span className="text-[13px] text-slate-400 italic">—</span>;
    }
    return (
      <span
        className="block text-[13px] text-slate-700 truncate"
        title={remarkText}
      >
        {remarkText}
      </span>
    );
  };

  const renderRemarkButton = (session, day) => {
    const remarkText = getRemarkText(session);
    if (!SHOW_SESSION_REMARK_UI || !canEditRemark || !onRemarkEdit) return null;

    return (
      <button
        type="button"
        onClick={() =>
          onRemarkEdit({
            ...session,
            date: day.date,
            attendanceId: session.attendanceId,
          })
        }
        className={DASHBOARD_BTN.iconRemark}
        title={remarkText ? "Edit remark" : "Add remark"}
        aria-label={remarkText ? "Edit remark" : "Add remark"}
      >
        {remarkText ? <PenIcon size={12} /> : <NotesIcon size={12} />}
      </button>
    );
  };

  const renderRemarkCell = (session, day) => {
    if (!SHOW_SESSION_REMARK_UI) return null;
    const showBtnInRemarkCol =
      canEditRemark && onRemarkEdit && !hasAction;

    return (
      <div className="flex items-center gap-1 w-full min-w-0">
        <div className="min-w-0 flex-1 overflow-hidden">
          {renderRemarkText(session)}
        </div>
        {showBtnInRemarkCol ? renderRemarkButton(session, day) : null}
      </div>
    );
  };

  const renderActionCell = (session, day, { isEmptyDay = false } = {}) => {
    if (isEmptyDay) {
      if (!onAdd) return null;
      return (
        <button
          type="button"
          onClick={() => onAdd({ date: day.date })}
          className={DASHBOARD_BTN.iconAdd}
          title="Add session"
          aria-label="Add session"
        >
          <PlusIcon size={12} />
        </button>
      );
    }

    const remarkBtn =
      SHOW_SESSION_REMARK_UI && hasAction && canEditRemark && onRemarkEdit
        ? renderRemarkButton(session, day)
        : null;
    const editBtn = onEdit ? (
      <button
        type="button"
        className={DASHBOARD_BTN.iconSession}
        onClick={() =>
          onEdit({
            ...session,
            date: day.date,
            attendanceId: session.attendanceId,
          })
        }
        title="Edit session times"
        aria-label="Edit session times"
      >
        <ClockIcon size={12} />
      </button>
    ) : null;

    if (!remarkBtn && !editBtn) return null;

    return (
      <div className="inline-flex items-center justify-center gap-1 flex-nowrap">
        {remarkBtn}
        {editBtn}
      </div>
    );
  };

  const renderDateCell = (day) => {
    const d = formatDate(day.date);
    return (
      <div className="flex items-center gap-2 min-w-[88px]">
        <div className="w-8 h-8 bg-indigo-50 text-indigo-700 rounded-md flex items-center justify-center font-semibold text-[13px] shrink-0 border border-indigo-100">
          {d.day}
        </div>
        <div className="min-w-0 leading-tight">
          <div className="text-[13px] font-medium text-slate-800">{d.shortWeekday}</div>
          <div className="text-[13px] text-slate-500">
            {d.monthShort} {d.year}
          </div>
        </div>
      </div>
    );
  };

  const statItems = [
    {
      label: "Total days",
      value: summary.totalDays || 0,
      valueClass: "text-slate-800",
    },
    {
      label: "Present",
      value: summary.presentDays || 0,
      valueClass: "text-emerald-600",
    },
    {
      label: "Work time",
      value: summary.totalWorkTime || "00:00:00",
      valueClass: "text-indigo-600 font-mono",
    },
    {
      label: "Break time",
      value: summary.totalBreakTime || "00:00:00",
      valueClass: "text-amber-600 font-mono",
    },
  ];

  const thClass = DASHBOARD_TABLE.headCell;
  const tdClass = DASHBOARD_TABLE.bodyCell;
  const tdTop = DASHBOARD_TABLE.bodyCellTop;
  const hasAction = Boolean(onEdit || onAdd);
  const showRemarkCol = SHOW_SESSION_REMARK_UI;
  const cols = getTimeCardColWidths(embedded, hasAction);
  /** Data columns after Date (session…work [+remark]) for empty-day colspan */
  const emptyDayColSpan = showRemarkCol ? 6 : 5;
  /** Day-total footer colspan (session…work [+remark] [+action]) */
  const dayTotalColSpan =
    emptyDayColSpan + (hasAction ? 1 : 0);

  return (
    <div className={embedded ? "space-y-2" : "space-y-2.5"}>
      {!embedded && (
      <div className="bg-white rounded-lg border border-indigo-100 px-3 py-2">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
          {userName && !embedded ? (
            <div className="flex items-center gap-2 min-w-0">
              <CalendarIcon className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-indigo-800 truncate">
                  {userName}
                </p>
                {(summary.month || summary.year) && (
                  <p className="text-[13px] text-slate-500">
                    {summary.month &&
                      new Date(2024, summary.month - 1).toLocaleString(
                        "default",
                        { month: "long" },
                      )}{" "}
                    {summary.year}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="hidden lg:block" />
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {statItems.map((item) => (
              <div
                key={item.label}
                className="inline-flex items-baseline gap-1.5 text-[13px]"
              >
                <span className="text-slate-500">{item.label}:</span>
                <span className={`font-semibold tabular-nums ${item.valueClass}`}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      <div className="bg-white rounded-lg border border-indigo-100 overflow-hidden">
        {days.length === 0 ? (
          <div className="py-10 text-center">
            <CalendarIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-[13px] font-medium text-slate-600">No activity found</p>
            <p className="text-[13px] text-slate-500 mt-0.5">
              No time card data for this period.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full text-left border-collapse table-fixed ${embedded ? "min-w-[760px]" : ""}`}>
              <colgroup>
                <col style={{ width: cols.date }} />
                <col style={{ width: cols.session }} />
                <col style={{ width: cols.clockIn }} />
                <col style={{ width: cols.clockOut }} />
                <col style={{ width: cols.breaks }} />
                <col style={{ width: cols.work }} />
                {showRemarkCol && cols.notes ? (
                  <col style={{ width: cols.notes }} />
                ) : null}
                {hasAction && cols.action ? (
                  <col style={{ width: cols.action }} />
                ) : null}
              </colgroup>
              <thead className={`border-b ${DASHBOARD_TABLE.headRow}`}>
                <tr>
                  <th className={thClass}>Date</th>
                  <th className={`${thClass} text-center`}>Session</th>
                  <th className={`${thClass} text-center`}>Clock in</th>
                  <th className={`${thClass} text-center`}>Clock out</th>
                  <th className={thClass}>Breaks</th>
                  <th className={`${thClass} text-right`}>Work duration</th>
                  {showRemarkCol ? <th className={thClass}>Remark</th> : null}
                  {(onEdit || onAdd) && (
                    <th
                      className={`${thClass} text-center whitespace-nowrap px-1`}
                      style={{ width: cols.action, minWidth: cols.action }}
                    >
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {days.map((day) => {
                  const hasSessions = day.sessions && day.sessions.length > 0;
                  const sessionCount = day.sessions?.length || 0;

                  if (!hasSessions) {
                    return (
                      <tr
                        key={day.date}
                        className="hover:bg-indigo-50/40 transition-colors"
                      >
                        <td className={tdClass}>{renderDateCell(day)}</td>
                        <td
                          colSpan={emptyDayColSpan}
                          className={`${tdClass} text-center text-slate-400 text-[13px] italic`}
                        >
                          No sessions recorded
                        </td>
                        {(onEdit || onAdd) && onAdd && (
                          <td
                            className={`${tdClass} text-center whitespace-nowrap px-1`}
                            style={{ width: cols.action, minWidth: cols.action }}
                          >
                            {renderActionCell(null, day, { isEmptyDay: true })}
                          </td>
                        )}
                        {(onEdit || onAdd) && !onAdd && (
                          <td className={tdClass} />
                        )}
                      </tr>
                    );
                  }

                  return (
                    <React.Fragment key={day.date}>
                      {day.sessions.map((session, sessionIdx) => {
                        const sessionIndex = sessionIdx + 1;
                        const isActive =
                          session.clockInTime && !session.clockOutTime;
                        const isFirstSession = sessionIdx === 0;

                        return (
                          <tr
                            key={`${day.date}-${sessionIndex}`}
                            className={`hover:bg-indigo-50/30 transition-colors ${isActive ? "bg-emerald-50/40" : ""}`}
                          >
                            {isFirstSession && (
                              <td
                                rowSpan={
                                  sessionCount > 1
                                    ? sessionCount + 1
                                    : sessionCount
                                }
                                className={`${tdTop} border-r border-indigo-50`}
                              >
                                {renderDateCell(day)}
                              </td>
                            )}

                            <td className={`${tdClass} text-center`}>
                              <span className="inline-block px-2 py-0.5 rounded text-[13px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                                S{sessionIndex}
                              </span>
                            </td>

                            <td className={`${tdClass} text-center tabular-nums`}>
                              {session.clockInTime
                                ? formatTime(session.clockInTime)
                                : "—"}
                            </td>

                            <td className={`${tdClass} text-center tabular-nums`}>
                              {session.clockOutTime ? (
                                formatTime(session.clockOutTime)
                              ) : (
                                <span className="text-[13px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                  Active
                                </span>
                              )}
                            </td>

                            <td className={tdClass}>{renderBreaks(session.breaks || [])}</td>

                            <td className={`${tdClass} text-right whitespace-nowrap`}>
                              <span className="font-mono text-[13px] font-semibold text-indigo-800 tabular-nums">
                                {session.totalWorkTime || "00:00:00"}
                              </span>
                            </td>

                            {showRemarkCol ? (
                              <td className={tdClass}>
                                {renderRemarkCell(session, day)}
                              </td>
                            ) : null}

                            {(onEdit || onAdd) && (
                              <td
                                className={`${tdClass} text-center whitespace-nowrap px-1`}
                                style={{ width: cols.action, minWidth: cols.action }}
                              >
                                {renderActionCell(session, day)}
                              </td>
                            )}
                          </tr>
                        );
                      })}

                      {sessionCount > 1 && (
                        <tr className="bg-indigo-50/30">
                          <td
                            colSpan={dayTotalColSpan}
                            className="px-2.5 py-2.5"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
                              <span className="text-slate-600 font-medium">
                                Day total ({sessionCount} sessions)
                              </span>
                              <div className="flex items-center gap-3">
                                <span className="text-slate-600">
                                  Work:{" "}
                                  <strong className="text-indigo-700 font-mono">
                                    {day.dayTotalWorkTime}
                                  </strong>
                                </span>
                                <span className="text-slate-600">
                                  Break:{" "}
                                  <strong className="text-amber-700 font-mono">
                                    {day.dayTotalBreakTime}
                                  </strong>
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeCardTable;
